import * as React from "react"
import { ChevronDown, ChevronUp } from "lucide-react"

import { Button } from "@/components/ui/button"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
} from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import type { AlfredBulkImportSelection, AlfredBulkImportRow } from "@/types/alfred-bulk-import"

const IDENTITY_FIELDS = new Set(["clientId", "deviceId", "sotiDeviceId"])
const PAGE_SIZE = 50

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

function getOptionalValues(row: AlfredBulkImportRow) {
  return Object.entries(row).filter(
    ([field, value]) => !IDENTITY_FIELDS.has(field) && value.trim() !== "",
  )
}

interface AlfredBulkPreviewStepProps {
  selection: AlfredBulkImportSelection
  onBack: () => void
  onConfirm: () => void
}

export function AlfredBulkPreviewStep({
  selection,
  onBack,
  onConfirm,
}: AlfredBulkPreviewStepProps) {
  const [expandedRows, setExpandedRows] = React.useState<Set<number>>(() => new Set())
  const [page, setPage] = React.useState(1)
  const { file, validation } = selection
  const totalPages = Math.ceil(validation.rows.length / PAGE_SIZE)
  const startIndex = (page - 1) * PAGE_SIZE
  const endIndex = Math.min(startIndex + PAGE_SIZE, validation.rows.length)
  const visibleRows = validation.rows.slice(startIndex, endIndex)

  const toggleRow = (rowNumber: number) => {
    setExpandedRows((current) => {
      const next = new Set(current)
      if (next.has(rowNumber)) next.delete(rowNumber)
      else next.add(rowNumber)
      return next
    })
  }

  return (
    <Card className="w-full max-w-5xl">
      <CardHeader className="flex flex-col gap-2">
        <h2 className="text-xl font-semibold text-foreground">Preview devices</h2>
        <CardDescription>
          Confirm the device identities and review any populated optional values before importing.
        </CardDescription>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
          <p className="font-medium text-foreground">{file.name}</p>
          <p className="text-muted-foreground">{formatFileSize(file.size)}</p>
          <p className="text-muted-foreground">
            {validation.rows.length === 1
              ? "1 device"
              : `${validation.rows.length} devices`}
          </p>
        </div>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">Row</TableHead>
              <TableHead>Client ID</TableHead>
              <TableHead>Device ID</TableHead>
              <TableHead>SOTI Device ID</TableHead>
              <TableHead className="text-right">Optional values</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleRows.map((row, index) => {
              const rowNumber = startIndex + index + 2
              const optionalValues = getOptionalValues(row)
              const isExpanded = expandedRows.has(rowNumber)

              return (
                <React.Fragment key={`${row.clientId}-${row.deviceId}-${rowNumber}`}>
                  <TableRow>
                    <TableCell>{rowNumber}</TableCell>
                    <TableCell>{row.clientId}</TableCell>
                    <TableCell>{row.deviceId}</TableCell>
                    <TableCell>{row.sotiDeviceId}</TableCell>
                    <TableCell className="text-right">
                      {optionalValues.length > 0 ? (
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          aria-expanded={isExpanded}
                          aria-label={`${isExpanded ? "Hide" : "Show"} optional values for row ${rowNumber}`}
                          onClick={() => toggleRow(rowNumber)}
                        >
                          {optionalValues.length}
                          {isExpanded ? (
                            <ChevronUp data-icon="inline-end" />
                          ) : (
                            <ChevronDown data-icon="inline-end" />
                          )}
                        </Button>
                      ) : (
                        <span className="text-muted-foreground">None</span>
                      )}
                    </TableCell>
                  </TableRow>
                  {isExpanded ? (
                    <TableRow className="bg-muted/30 hover:bg-muted/30">
                      <TableCell colSpan={5}>
                        <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                          {optionalValues.map(([field, value]) => (
                            <div key={field} className="flex flex-col gap-1">
                              <dt className="text-xs font-medium text-muted-foreground">{field}</dt>
                              <dd className="break-words text-sm text-foreground">{value}</dd>
                            </div>
                          ))}
                        </dl>
                      </TableCell>
                    </TableRow>
                  ) : null}
                </React.Fragment>
              )
            })}
          </TableBody>
        </Table>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-muted-foreground">
            Showing {startIndex + 1}–{endIndex} of {validation.rows.length} devices
          </p>
          {totalPages > 1 ? (
            <div className="flex items-center gap-2">
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-label="Previous page"
                disabled={page === 1}
                onClick={() => setPage((current) => Math.max(1, current - 1))}
              >
                Previous
              </Button>
              <Button
                type="button"
                variant="outline"
                size="sm"
                aria-label="Next page"
                disabled={page === totalPages}
                onClick={() => setPage((current) => Math.min(totalPages, current + 1))}
              >
                Next
              </Button>
            </div>
          ) : null}
        </div>
      </CardContent>
      <CardFooter className="flex flex-wrap justify-between gap-3">
        <Button type="button" variant="outline" onClick={onBack}>
          Back to upload
        </Button>
        <Button type="button" onClick={onConfirm}>
          Confirm import
        </Button>
      </CardFooter>
    </Card>
  )
}
